import Vue from 'vue'
import { firestoreAction } from 'vuexfire'
import * as firebase from 'firebase'
import db from '@/db'
import { setTimeout } from 'timers'

const ratesRef = db.collection('rates')
const orgRef = db.collection('organization')
const updated = firebase.default.firestore.FieldValue.serverTimestamp()

const initialState = {
  rates: [],
  ratesLoading: true
}

const state = Object.assign({}, initialState)

const mutations = {
  'SET_RATES_LOADING' (state, loading) {
    state.ratesLoading = loading
  },
  'UPDATE_RATE_VIEW' (state, payload) {
    const rateCard = state.rates.findIndex(rates => rates.id === payload.id)
    Vue.set(state.rates, rateCard, payload)
  },
  'RESET_STATE' (state) {
    for (let prop in state) {
      state[prop] = initialState[prop]
    }
  }
}

const actions = {
  resetRateState: firestoreAction(({ commit, unbindFirestoreRef }) => {
    unbindFirestoreRef('rates')
    commit('RESET_STATE')
  }),
  setRatesRef: firestoreAction(({ bindFirestoreRef, commit, dispatch }, ref) => {
    bindFirestoreRef('rates', ref).then(response => {
      commit('SET_RATES_LOADING', false)
      dispatch('toggleLoading', false)
    }).catch(error => {
      console.log(error)
    })
  }),
  addRates: ({ dispatch }, payload) => {
    let rates = { ...payload }
    return new Promise((resolve, reject) => {
      ratesRef.add(rates).then(entry => {
        const id = entry.id
        let rateInfo = {
          updated: updated
        }
        const rateKey = 'rates.' + id
        rateInfo[rateKey] = true
        orgRef.doc(payload.organization).update(rateInfo).then(response => {
          resolve(id)
          dispatch('saveState', true)
        }).catch(error => {
          console.log(error)
        })
      }, error => {
        reject(error)
      })
    })
  },
  editRateCard: ({ dispatch }, payload) => {
    let updatedCard = { ...payload }
    updatedCard.updated = updated
    ratesRef.doc(updatedCard.id).update(updatedCard).then(item => {
      dispatch('saveState', true)
    }).catch(error => {
      console.log(error)
    })
  },
  editRates: ({ commit, dispatch }, payload) => {
    const oldRate = { ...payload }
    const newRate = { ...oldRate }
    const percent = value => value / 100

    // Assign current numbers to object
    const current = { ...oldRate.current }

    // Calculate current base numbers
    current.defecit = current.incoming > current.outgoing ? 0 : current.outgoing - current.incoming
    current.profit = current.defecit > 0 ? 0 : current.incoming - current.outgoing
    current.operating = current.outgoing
    current.incomingPerEmployee = current.incoming / current.fullTimeEmployees
    current.outgoingPerEmployee = current.outgoing / current.fullTimeEmployees
    current.operatingPerEmployee = current.operating / current.fullTimeEmployees
    current.incomingPerClient = current.incoming / current.clients
    current.outgoingPerClient = current.outgoing / current.clients
    // Estimates
    current.salaries = current.averageSalary * current.fullTimeEmployees
    current.expenses = current.operating - current.salaries
    current.expensesPerEmployee = current.expenses / current.fullTimeEmployees

    // Calculate current proportions
    current.profitProportion = current.profit / current.incoming
    current.defecitProportion = current.defecit > 0 ? current.defecit / current.incoming : 0
    current.operatingProportion = current.operating / current.incoming
    // Estimates
    current.salaryProportion = current.salaries / current.incoming
    current.expensesProportion = current.expenses / current.incoming

    newRate.current = current

    // Assign goal numbers to object
    const goal = { ...oldRate.goal }

    // Calculate our ideal proportions
    let operatingProportion = 100 - oldRate.goal.profitProportion

    if (operatingProportion < 0) {
      const newTotal = oldRate.goal.profitProportion + (operatingProportion * -1)
      oldRate.goal.profitProportion = oldRate.goal.profitProportion / newTotal * 100
      operatingProportion = (operatingProportion * -1) / newTotal * 100
    }
    goal.operatingProportion = percent(operatingProportion)
    goal.profitProportion = percent(oldRate.goal.profitProportion)

    // Calculate our goals
    if (!oldRate.goal.weeksOff) {
      goal.weeksOff = 52 - goal.workingWeeks
    }
    goal.salaries = goal.averageSalary * goal.fullTimeEmployees
    goal.expensesPerEmployee = current.expensesPerEmployee
    goal.expenses = goal.expensesPerEmployee * goal.fullTimeEmployees
    goal.operating = goal.expenses + goal.salaries
    goal.operatingPerEmployee = goal.operating / goal.fullTimeEmployees
    goal.incoming = goal.operating / goal.operatingProportion
    goal.incomingPerEmployee = goal.incoming / goal.fullTimeEmployees
    goal.outgoing = goal.operating
    goal.outgoingPerEmployee = goal.outgoing / goal.fullTimeEmployees
    goal.profit = goal.incoming - goal.outgoing
    goal.incomingPerClient = goal.incoming / goal.clients
    goal.outgoingPerClient = goal.outgoing / goal.clients
    goal.salaryProportion = goal.salaries / goal.incoming
    goal.expensesProportion = goal.expenses / goal.incoming
    goal.workingSprints = goal.workingWeeks / 2

    // Divide income and outgoing by time frames
    goal.monthIncoming = goal.incoming / 12
    goal.monthOutgoing = goal.outgoing / 12
    goal.sprintIncoming = goal.incoming / goal.workingSprints
    goal.sprintOutgoing = goal.outgoing / goal.workingSprints
    goal.weekIncoming = goal.incoming / goal.workingWeeks
    goal.weekOutgoing = goal.outgoing / goal.workingWeeks
    goal.dayIncoming = goal.weekIncoming / 5
    goal.dayOutgoing = goal.weekOutgoing / 5
    goal.hourIncoming = goal.weekIncoming / goal.billableHours
    goal.hourOutgoing = goal.weekOutgoing / goal.billableHours

    newRate.goal = goal

    // Calculate rates
    const calculateRate = (total) => Math.round(total / goal.billableEmployees / goal.clientLoad)

    // Add to oject
    newRate.monthRate = calculateRate(goal.monthIncoming)
    newRate.monthBreakEven = calculateRate(goal.monthOutgoing)
    newRate.sprintRate = calculateRate(goal.sprintIncoming)
    newRate.sprintBreakEven = calculateRate(goal.sprintOutgoing)
    newRate.weekRate = calculateRate(goal.weekIncoming)
    newRate.weekBreakEven = calculateRate(goal.weekOutgoing)
    newRate.dayRate = Math.round(goal.dayIncoming / goal.billableEmployees)
    newRate.dayBreakEven = Math.round(goal.dayOutgoing / goal.billableEmployees)
    newRate.hourRate = Math.round(goal.hourIncoming / goal.billableEmployees)
    newRate.hourBreakEven = Math.round(goal.hourOutgoing / goal.billableEmployees)

    delete newRate.oldRate
    delete newRate.oldExpenses
    delete newRate.oldMonthRate
    delete newRate.oldRevenue

    return new Promise((resolve, reject) => {
      dispatch('editRateCard', newRate).then(response => {
        resolve('Success!')
      }, error => {
        reject(error)
      })
    })
  },
  archiveRates: ({ dispatch }, id) => {
    const update = {
      updated: updated,
      archived: updated
    }
    ratesRef.doc(id).update(update).then(response => {
      dispatch('saveState', true)
    }).catch(error => {
      console.log(error)
    })
  },
  duplicateRate: ({ dispatch }, payload) => {
    const rateData = { ...payload }
    rateData.name = payload.name + ' copy'
    rateData.updated = updated
    rateData.created = updated
    return new Promise((resolve, reject) => {
      ratesRef.add(rateData).then(entry => {
        const id = entry.id
        let rateInfo = {
          updated: updated
        }
        const rateKey = 'rates.' + id
        rateInfo[rateKey] = true
        orgRef.doc(payload.organization).update(rateInfo).then(response => {
          resolve(id)
        }).catch(error => {
          console.log(error)
        })
        setTimeout(delay => {
          dispatch('saveState', true)
          dispatch('toggleLoading', false)
        }, 2000)
      }, error => {
        reject(error)
      })
    })
  },
  duplicateRates: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      const oldRates = { ...state.rates }
      const rateMatrix = {}
      const batch = db.batch()
      let rateCount = 0
      Object.keys(oldRates).forEach(rate => {
        const newRate = { ...oldRates[rate] }
        const oldRate = oldRates[rate].id
        delete newRate.id
        newRate.owner = payload.user
        newRate.created = updated
        newRate.updated = updated
        newRate.organization = payload.org
        newRate.estimates = false
        ratesRef.add(newRate).then(reference => {
          rateMatrix[oldRate] = reference.id
          const org = orgRef.doc(payload.org)
          const orgKey = 'rates.' + reference.id
          const orgInfo = {}
          orgInfo[orgKey] = true
          batch.update(org, orgInfo)
          rateCount++
          if (rateCount === state.rates.length) {
            commitBatch()
          }
        })
        function commitBatch () {
          batch.commit().then(response => {
            resolve(rateMatrix)
          }, error => {
            reject(error)
          })
        }
      })
    })
  }
}

const getters = {
  ratesLoading: state => {
    return state.ratesLoading
  },
  rates: (state, rootGetters) => {
    return state.rates.filter(rates => rates.archived === false)
  },
  rateCard: (state, getters) => (id) => {
    return state.rates.find(rates => rates.id === id)
  },
  rateList: (state, getters, rootGetters) => {
    return state.rates.filter(rates => rates.archived === false).map(rate => {
      return {
        value: rate.id,
        label: rate.name,
        type: rate.type
      }
    })
  },
  rateUnits: (state) => (key) => {
    if (key && key !== 'zzzzz') {
      const rates = state.rates.find(rates => rates.id === key)
      return [
        {
          value: 0,
          label: 'Hour',
          amount: rates.hourRate,
          breakEven: rates.hourBreakEven
        },
        {
          value: 1,
          label: 'Day',
          amount: rates.dayRate,
          breakEven: rates.dayBreakEven
        },
        {
          value: 2,
          label: 'Week',
          amount: rates.weekRate,
          breakEven: rates.weekBreakEven
        },
        {
          value: 3,
          label: 'Sprint',
          amount: rates.sprintRate,
          breakEven: rates.sprintBreakEven
        },
        {
          value: 4,
          label: 'Month',
          amount: rates.monthRate,
          breakEven: rates.monthBreakEven
        }
      ]
    }
    return [
      {
        value: 0,
        label: 'Hour'
      },
      {
        value: 1,
        label: 'Day'
      },
      {
        value: 2,
        label: 'Week'
      },
      {
        value: 3,
        label: 'Sprint'
      },
      {
        value: 4,
        label: 'Month'
      }
    ]
  },
  revenueProportions: (state) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    let profit = rate.goal.profitProportion
    // Find out why I even need to do this. Seems like its cycling before being submitted
    if (rate.goal.profitProportion < 1) {
      profit = Math.round(rate.goal.profitProportion * 100)
    }
    const currentProfit = Math.round(rate.current.profitProportion * 100)
    const expenses = Math.round(rate.goal.expensesProportion * 100)
    const currentExpenses = Math.round(rate.current.expensesProportion * 100)
    const salary = Math.round(rate.goal.salaryProportion * 100)
    const currentSalary = Math.round(rate.current.salaryProportion * 100)
    const currentDefecit = Math.round(rate.current.defecitProportion * 100)
    const operating = Math.round(rate.goal.operatingProportion * 100)
    const proportions = {
      id: rate.id,
      profit: profit,
      currentProfit: currentProfit,
      expenses: expenses,
      currentExpenses: currentExpenses,
      salary: salary,
      operating: operating,
      currentSalary: currentSalary,
      currentDefecit: currentDefecit
    }
    return proportions
  },
  annualRevenueGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Incoming',
          backgroundColor: '#00AA13',
          data: [Math.round(rate.current.incoming), Math.round(rate.goal.incoming)]
        },
        {
          label: 'Outgoing',
          backgroundColor: '#9A0000',
          data: [Math.round(rate.current.outgoing), Math.round(rate.goal.outgoing)]
        }
      ]
    }
  },
  profitGrowthGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Incoming',
          borderColor: '#00AA13',
          fill: false,
          data: [Math.round(rate.current.incoming), Math.round(rate.goal.incoming)]
        },
        {
          label: 'Outgoing',
          borderColor: '#9A0000',
          fill: false,
          data: [Math.round(rate.current.outgoing), Math.round(rate.goal.outgoing)]
        }
      ]
    }
  },
  goalRevenueBreakdownChart: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    let profit = rate.goal.profitProportion
    // Find out why I even need to do this. Seems like its cycling before being submitted
    if (rate.goal.profitProportion < 1) {
      profit = Math.round(rate.goal.profitProportion * 100)
    }
    return {
      labels: [
        'Salaries',
        'Operating expenses',
        'Profit'
      ],
      datasets: [
        {
          backgroundColor: [
            '#FF592D',
            '#9A0000',
            '#00AA13'
          ],
          borderColor: [
            '#231F20',
            '#231F20',
            '#231F20'
          ],
          data: [
            Math.round(rate.goal.salaryProportion * 100),
            Math.round(rate.goal.expensesProportion * 100),
            profit
          ]
        }
      ]
    }
  },
  currentRevenueBreakdownChart: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    const salaries = Math.round(rate.current.salaryProportion * 100)
    const profit = Math.round(rate.current.profitProportion * 100)
    const defecit = Math.round(rate.current.defecitProportion * 100)
    const expenses = Math.round(rate.current.expensesProportion * 100)
    return {
      labels: [
        'Defecit',
        'Salaries',
        'Operating expenses',
        'Profit'
      ],
      datasets: [
        {
          backgroundColor: [
            '#322E2F',
            '#FF592D',
            '#9A0000',
            '#00AA13'
          ],
          borderColor: [
            '#231F20',
            '#231F20',
            '#231F20',
            '#231F20'
          ],
          data: [
            defecit,
            salaries,
            expenses,
            profit
          ]
        }
      ]
    }
  },
  rfteGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Revenue per employee',
          backgroundColor: '#00AA13',
          data: [Math.round(rate.current.incomingPerEmployee), Math.round(rate.goal.incomingPerEmployee)]
        },
        {
          label: 'Expenses per employee',
          backgroundColor: '#9A0000',
          data: [Math.round(rate.current.outgoingPerEmployee), Math.round(rate.goal.outgoingPerEmployee)]
        }
      ]
    }
  },
  rfteGrowthGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Revenue',
          borderColor: '#00AA13',
          fill: false,
          data: [Math.round(rate.current.incomingPerEmployee), Math.round(rate.goal.incomingPerEmployee)]
        },
        {
          label: 'Expenses',
          borderColor: '#9A0000',
          fill: false,
          data: [Math.round(rate.current.outgoingPerEmployee), Math.round(rate.goal.outgoingPerEmployee)]
        }
      ]
    }
  },
  clientSizeGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Revenue',
          backgroundColor: '#00AA13',
          data: [Math.round(rate.current.incoming / rate.current.clients), Math.round(rate.goal.incoming / rate.goal.clients)]
        },
        {
          label: 'Break even',
          backgroundColor: '#9A0000',
          data: [Math.round(rate.current.outgoing / rate.current.clients), Math.round(rate.goal.outgoing / rate.goal.clients)]
        }
      ]
    }
  },
  clientGrowthGraph: (state, getters) => (id) => {
    const rate = state.rates.find(rates => rates.id === id)
    return {
      labels: [
        'Current',
        'Goal'
      ],
      datasets: [
        {
          label: 'Revenue',
          borderColor: '#00AA13',
          fill: false,
          data: [Math.round(rate.current.incoming / rate.current.clients), Math.round(rate.goal.incoming / rate.goal.clients)]
        },
        {
          label: 'Break even',
          borderColor: '#9A0000',
          fill: false,
          data: [Math.round(rate.current.outgoing / rate.current.clients), Math.round(rate.goal.outgoing / rate.goal.clients)]
        }
      ]
    }
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}
